import {
  camelCase,
  cloneDeep,
  difference,
  fromPairs,
  keyBy,
  keys,
  startCase,
  union,
  uniq,
  intersection,
} from 'lodash';
import { AccountInitDataProjectsQuery } from 'api/account';
import { FieldSectionType } from 'api/field-sections/types';
import { CompanyId, ProjectId } from 'api/models';
import { User, Team, MemberType, CompanyType } from 'api/models/core-types';
import { ObjectType } from 'api/pushes';
import { Epic as EpicType } from 'api/sprint-planning/types';
import {
  Field as ApiField,
  FieldOptionBase,
  StatusFieldOption,
} from 'api/tasks';
import {
  FieldType,
  filterableFieldType,
  SourceType,
} from 'api/tasks/field-meta';
import type {
  FilterableField,
  SingleSelectField,
} from 'api/tasks/filterable-field';
import type { UsersMetadata } from 'api/tasks/users-metadata';
import { ProjectInUserProfile } from 'api/user';
import {
  FieldDataType,
  FieldTypes as ModuleFieldTypes,
} from 'components/organisms/module-settings/fields/types';
import { Translate, TranslationKey } from 'hooks/use-translation';
import { CompaniesStore, Company } from 'stores/companies';
import {
  EditableContent,
  Empty,
  Loaded,
  Loading,
  RefreshableContent,
  Refreshing,
} from 'stores/core';
import { MembershipsStore } from 'stores/memberships';
import { TeamsStore } from 'stores/teams';
import { UsersStore } from 'stores/users';
import { FieldHelper } from 'utils/field';
import { getRandomColor } from 'utils/get-random-color';
import { constructUserMeta } from './util';

type FieldBase = FilterableField &
  // TODO: stop using this
  Pick<
    ApiField,
    | 'fieldDataType'
    | 'fieldType'
    | 'optionsAvailable'
    | 'isEnabled'
    | 'isEditable'
  >;
export type TaskField = FieldBase & { objectType: ObjectType.Task };
export type ProjectField = FieldBase & { objectType: ObjectType.Project };
export type ProjectPhaseField = FieldBase & {
  objectType: ObjectType.ProjectPhase;
};
export type Field = TaskField | ProjectField | ProjectPhaseField;
export type SprintField = FieldBase & { ObjectType: ObjectType.Sprint };
export type EpicField = FieldBase & { ObjectType: ObjectType.Epic };
interface ByObjectType {
  ids: number[];
  enabled: number[];
  byFieldName: Record<string, number>;
  byCustomField: {
    true: number[];
    false: number[];
  };
}

export type FieldsStore = {
  ids: RefreshableContent<number[]>;
  byId: Record<number, EditableContent<Field>>;
  originals: {
    byId: Record<number, ApiField>;
    ids: number[];
  };
  byObjectType: {
    [ObjectType.Task]: ByObjectType;
    [ObjectType.Project]: ByObjectType;
    [ObjectType.ProjectPhase]: ByObjectType;
  };
};

const EMPTY: FieldsStore = {
  byId: {},
  originals: {
    byId: {},
    ids: [],
  },
  ids: Empty(),
  byObjectType: {
    [ObjectType.Task]: {
      ids: [],
      enabled: [],
      byFieldName: {},
      byCustomField: {
        true: [],
        false: [],
      },
    },
    [ObjectType.Project]: {
      ids: [],
      enabled: [],
      byFieldName: {},
      byCustomField: {
        true: [],
        false: [],
      },
    },
    [ObjectType.ProjectPhase]: {
      ids: [],
      enabled: [],
      byFieldName: {},
      byCustomField: {
        true: [],
        false: [],
      },
    },
  },
};

export const FIELD_ID_ASSIGNEE = -127;
const FIELD_ID_CREATED_AT = FIELD_ID_ASSIGNEE + 1;
const FIELD_ID_CREATED_BY = FIELD_ID_CREATED_AT + 1;
const FIELD_ID_PROJECT = FIELD_ID_CREATED_BY + 1;
const FIELD_ID_RESPONSIBLE = FIELD_ID_PROJECT + 1;
const FIELD_ID_PRIVATE = FIELD_ID_RESPONSIBLE + 1;

export const FIELD_ID_PROJECT_PHASE_FIELD = FIELD_ID_PRIVATE + 1;
const JIRA_INTEGRATION_FIELD = FIELD_ID_PROJECT_PHASE_FIELD + 1;
const COMPLETED_LATE_BY = JIRA_INTEGRATION_FIELD + 1;

export const FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID = COMPLETED_LATE_BY + 1;
const FIELD_ID_TIME_TRACKED = FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID + 1;
const FIELD_ID_TASK_DEPENDENCY_STATUS = FIELD_ID_TIME_TRACKED + 1;
const FIELD_ID_ASSOCIATED_SPRINT = FIELD_ID_TASK_DEPENDENCY_STATUS + 1;

export const FIELD_ID_CURRENT_SPRINT = FIELD_ID_ASSOCIATED_SPRINT + 1;
const FIELD_ID_BUDGETS = FIELD_ID_CURRENT_SPRINT + 1;
const FIELD_ID_BUDGET_NAMES = FIELD_ID_BUDGETS + 1;
const FIELD_ID_EPIC = FIELD_ID_BUDGET_NAMES + 1;
const FIELD_ID_APPLY_TO_SUBTASK = FIELD_ID_EPIC + 1;
const FIELD_ID_REMAINING_HOURS = FIELD_ID_APPLY_TO_SUBTASK + 1;
const FIELD_ID_TASK_NAME = FIELD_ID_REMAINING_HOURS + 1;
const FIELD_ID_MILESTONE_REV_REC = FIELD_ID_TASK_NAME + 1;
const FIELD_ID_MILESTONE_REV_REC_AMOUNT = FIELD_ID_MILESTONE_REV_REC + 1;
// Add new fields here after FIELD_ID_MILESTONE_REV_REC_AMOUNT
const FIELD_ID_TOTAL_ALLOCATED_MINUTES = FIELD_ID_MILESTONE_REV_REC_AMOUNT + 1;

enum FieldLabel {
  CreatedOn = 'Created on',
  StartedOn = 'Started on',
  HoursTracked = 'Hours Tracked',
  AllocatedHours = 'Allocated hours',
  RemainingHours = 'Remaining Hours',
  Responsible = 'Responsible',
  ProjectPhase = 'Project Phase',
  JiraIssue = 'Jira Issue',
  DaysCompletedLateBy = 'Days completed late by',
  TaskDependencyStatus = 'Blocking task status',
  AssociatedSprints = 'Associated sprints',
  CurrentSprint = 'Current sprint',
  Budgets = 'Budgets',
  Epic = 'Epic',
  ApplyToSubtask = 'Apply to subtask',
  RevRecPlan = 'RevRec Task',
  RevRecAmount = 'RevRec Amount',
}

const CreatedAtField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'DATE',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Date,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_CREATED_AT,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'createdAt',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('created_on' as TranslationKey)
    : FieldLabel.CreatedOn,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

const CreatedByField = (
  users: UsersMetadata,
  translate: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate('created_by' as TranslationKey),
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'USER',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.SingleUser,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_CREATED_BY,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'createdBy',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    users,
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});
const TimeTrackedField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'NUMBER',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Number,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_TIME_TRACKED,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'timeEntryMinutes',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('hours_tracked' as TranslationKey)
    : FieldLabel.HoursTracked,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

const TotalAllocatedMinutesField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'NUMBER',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Number,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_TOTAL_ALLOCATED_MINUTES,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'totalAllocatedMinutes',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('project.fields.allocated_hours' as TranslationKey)
    : FieldLabel.AllocatedHours,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

export const AssociatedSprintsField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'MULTI_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.MultiSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_ASSOCIATED_SPRINT,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'associatedSprints',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('associated_sprints' as TranslationKey)
    : FieldLabel.AssociatedSprints,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: [],
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

export const CurrentSprintField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_CURRENT_SPRINT,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'sprint',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('current_sprint' as TranslationKey)
    : FieldLabel.CurrentSprint,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: [],
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

export const EpicField = (
  epics: Pick<EpicType, 'epicName' | 'epicId'>[],
  isEpicEditable: boolean,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_EPIC,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'epic',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: FieldLabel.Epic,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: epics.map((x) => ({
      label: x.epicName,
      value: x.epicId,
      color: getRandomColor(x.epicId),
      active: true,
    })),
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEditable: isEpicEditable,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});
const RemainingHoursField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'NUMBER',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Number,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_REMAINING_HOURS,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'remainingEffortInMinutes',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('project.fields.remaining_minutes')
    : FieldLabel.RemainingHours,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

export const BudgetsField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: '',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: '',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.MultiSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_BUDGETS,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'projectFinancialsBudgets',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('project.fields.budgets')
    : FieldLabel.Budgets,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: [],
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

export const BudgetNamesField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Text,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_BUDGET_NAMES,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'projectFinancialsBudgetName',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('project.fields.budgets')
    : FieldLabel.Budgets,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
});

export const ProjectField = (
  projects: Pick<ProjectInUserProfile, 'projectName' | 'projectId'>[],
  translate: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate('project' as TranslationKey),
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_PROJECT,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'project',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: projects.map((x) => ({
      label: x.projectName,
      value: x.projectId,
      color: getRandomColor(x.projectId),
      active: true,
    })),
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});
export const ProjectFieldAsTextField = (
  projects: Pick<ProjectInUserProfile, 'projectName' | 'projectId'>[],
  translate: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate('project' as TranslationKey),
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Text,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_PROJECT,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'project',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: projects.map((x) => ({
      label: x.projectName,
      value: x.projectId,
      color: getRandomColor(x.projectId),
      active: true,
    })),
  },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});

export const TaskNameField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_TASK_NAME,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'taskName',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectSubType: 'DEFAULT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate('task_name' as TranslationKey),
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Text,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
});

export const AssigneeField = (
  users: UsersMetadata,
  teams: Team[],
  projectPlaceholders: { [id: number]: Team[] },
  translate: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate('assignee' as TranslationKey),
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_ASSIGNEE,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: '',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: '',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Assignee,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'assignee',
  // @TODO remove the user and teams meta as whole when everthing has been migrated to indexedFb
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    users: {
      active_user_ids: [],
      by_project_id: {},
      by_user_id: {},
      non_external_user_ids: [],
    },
    teams,
    projectPlaceholders,
  },
  // metaData: { users, teams, projectPlaceholders },
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldGroup: '_native',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
});
const ResponsibleField = (
  companies: Company[],
  translate?: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_RESPONSIBLE,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'MULTI_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.MultiSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'responsible',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('responsible' as TranslationKey)
    : FieldLabel.Responsible,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  description: translate
    ? translate(
        'stores.describes_who_is_responsible_for_this_task' as TranslationKey,
      )
    : 'Describes who is responsible for this task',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: companies.map((x) => ({
      label: x.companyName,
      value: x.companyId,
      color: getRandomColor(x.companyId),
      active: true,
    })),
  },
});
const PrivateField = (translate: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'BOOLEAN',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Boolean,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_PRIVATE,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'private',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate('private' as TranslationKey),
});

const ProjectPhaseField = (translate?: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Text,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_PROJECT_PHASE_FIELD,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'phaseName',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('project_phase' as TranslationKey)
    : FieldLabel.ProjectPhase,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
});

const ProjectPhaseFieldWithId = (translate?: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'projectPhaseId',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('project_phase' as TranslationKey)
    : FieldLabel.ProjectPhase,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    options: [],
  },
});

const JiraIntegrationField = (translate?: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Text,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: JIRA_INTEGRATION_FIELD,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'jiraIntegration',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('jira_issue' as TranslationKey)
    : FieldLabel.JiraIssue,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
});

const CompletedLateBy = (translate?: Translate): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'NUMBER',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'NUMBER',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Number,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: COMPLETED_LATE_BY,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'completedLateBy',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('days_completed_late_by' as TranslationKey)
    : FieldLabel.DaysCompletedLateBy,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    required: false,
    rangeAvailable: false,
  },
});

const taskDependencyStatusField = (
  options,
  translate?: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: 'SINGLE_SELECT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: 'TEXT',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_TASK_DEPENDENCY_STATUS,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'taskDependencyStatus',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('blocking_task_status' as TranslationKey)
    : FieldLabel.TaskDependencyStatus,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  metaData: {
    required: false,
    options,
  },
});

const constructByObjectType = (fields: Field[]): ByObjectType => {
  const fields_lookup = keyBy(fields, 'fieldId');
  const ids = keys(fields_lookup).map(Number);

  return {
    byCustomField: {
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      true: ids.filter((x) => !!fields_lookup[x].customField),
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      false: ids.filter((x) => !fields_lookup[x].customField),
    },
    byFieldName: fromPairs(
      fields.map(({ fieldId, fieldName }) => [fieldName, fieldId]),
    ),
    // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
    enabled: ids.filter((x) => !!fields_lookup[x].isEnabled),
    ids,
  };
};

const load =
  () =>
  ({ byId, ids, originals, byObjectType }: FieldsStore): FieldsStore => ({
    byId,
    ids:
      ids[0] === 'refreshing' || ids[0] === 'loading'
        ? ids
        : ids[0] === 'loaded'
        ? Refreshing(ids)
        : Loading(),
    originals,
    byObjectType,
  });

const putAll =
  (originals: ApiField[]) =>
  (store: FieldsStore): FieldsStore => {
    const fields = originals.map((field) =>
      field.fieldName === 'defaultTimeEntryCategory'
        ? {
            ...field,
            metaData: {
              ...field?.metaData,
              options: (field?.metaData?.options ?? []).map((option) => ({
                ...option,
                value: (option as any).valueStr
                  ? Number((option as any).valueStr)
                  : option?.value,
                color: getRandomColor(
                  (option as any).valueStr
                    ? Number((option as any).valueStr)
                    : option?.value,
                ),
              })),
            },
          }
        : field.fieldName === 'startDateActual' &&
          field.objectType === FieldSectionType.Task &&
          field.fieldLabel === 'Start Date Actual'
        ? {
            ...field,
            fieldLabel: 'Started on',
          }
        : field,
    );

    return {
      ...store,
      originals: {
        byId: keyBy(fields, 'fieldId'),
        ids: fields.map(({ fieldId }) => fieldId),
      },
    };
  };

const add =
  (field: ApiField) =>
  ({ originals: { byId, ids }, ...store }: FieldsStore): FieldsStore => ({
    ...store,
    originals: {
      byId: { ...byId, [field.fieldId]: field },
      ids: union(ids, [field.fieldId]),
    },
  });

const remove =
  (fieldId: number) =>
  ({
    originals: {
      byId: { [fieldId]: deleted, ...rest },
      ids,
    },
    ...store
  }: FieldsStore): FieldsStore => ({
    ...store,
    originals: {
      byId: rest,
      ids: difference(ids, [fieldId]),
    },
  });

const ApplyToSubtaskField: TaskField = {
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: ModuleFieldTypes.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: FieldDataType.Boolean,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Boolean,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_APPLY_TO_SUBTASK,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'applyToSubtask',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: FieldLabel.ApplyToSubtask,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
};

const MilestoneRevenueRecognitionTaskField = (
  translate: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: ModuleFieldTypes.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: FieldDataType.Boolean,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Boolean,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_MILESTONE_REV_REC,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'isMilestoneRevenueRecognitionTask',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('task.rev_rec_plan' as TranslationKey)
    : FieldLabel.RevRecPlan,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEditable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
});

const MilestoneTaskRevenueRecognitionAmountField = (
  translate: Translate,
): TaskField => ({
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldType: ModuleFieldTypes.SingleSelect,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldDataType: FieldDataType.Number,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  customField: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  type: FieldType.Number,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldId: FIELD_ID_MILESTONE_REV_REC_AMOUNT,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldName: 'milestoneTaskRevenueRecognitionAmount',
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  fieldLabel: translate
    ? translate('task.rev_rec_amount' as TranslationKey)
    : FieldLabel.RevRecAmount,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  optionsAvailable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  private: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEnabled: true,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  isEditable: false,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  objectType: ObjectType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  sourceType: SourceType.Task,
  // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
  userCreated: false,
});

export const getBaseFields = ({
  users,
  teams,
  projectPlaceholders,
  createdByUsers,
  projectsArr,
  companies,
  statusFieldOptions,
  translate,
}: {
  users: UsersMetadata;
  teams: Team[];
  projectPlaceholders: { [id: number]: Team[] };
  translate: Translate;
  createdByUsers: UsersMetadata;
  projectsArr: ProjectInUserProfile[];
  companies: Company[];
  statusFieldOptions: FieldOptionBase<number>[];
  makeProjectFieldAsTextField?: boolean;
}) => [
  AssigneeField(users, teams, projectPlaceholders, translate),
  CreatedAtField(translate),
  CreatedByField(createdByUsers, translate),
  TimeTrackedField(translate),
  TotalAllocatedMinutesField(translate),
  ProjectField(projectsArr, translate),
  ResponsibleField(companies, translate),
  PrivateField(translate),
  JiraIntegrationField(translate),
  CompletedLateBy(translate),
  taskDependencyStatusField(statusFieldOptions, translate),
  ProjectPhaseField(translate),
  ProjectPhaseFieldWithId(translate),
  RemainingHoursField(translate),
  MilestoneRevenueRecognitionTaskField(translate),
  MilestoneTaskRevenueRecognitionAmountField(translate),
  ApplyToSubtaskField,
];

const refresh =
  (
    usersStore: UsersStore,
    teamsStore: TeamsStore,
    membershipsStore: MembershipsStore,
    companiesStore: CompaniesStore,
    [, currentCompanyId]: CompanyId,

    currentUser: User,

    systemBotId?: number,
    projectsData?: AccountInitDataProjectsQuery,
    filterPointsFieldsFromTaskFields?: (
      fields: (Field | ApiField)[],
    ) => (Field | ApiField)[],
    isMultilineBudgetFeatureEnabled: boolean = false,
    isSprintPlaningFeatureEnabled?: boolean,
    isEpicFeatureEnabled?: boolean,
    epicsArr?: EpicType[],
    translate: Translate = (key: string) => key,
  ) =>
  (store: FieldsStore): FieldsStore => {
    if (
      !TeamsStore.loaded(teamsStore) ||
      !UsersStore.loaded(usersStore) ||
      !CompaniesStore.loaded(companiesStore)
    ) {
      return store;
    }
    const users_list = UsersStore.toList(usersStore);
    const active_users = UsersStore.activeUsers(usersStore);
    const active_user_ids = active_users.map(({ userId }) => userId);
    const non_external_user_ids = active_users.map(({ userId }) => userId);
    const native_user_ids = UsersStore.ofMemberType(MemberType.Native)(
      usersStore,
    );
    const private_user_ids = UsersStore.ofCompanyId(
      CompanyId(currentCompanyId),
    )(usersStore);
    const private_active_user_ids = intersection(
      private_user_ids,
      active_user_ids,
    );
    const teams = TeamsStore.toList(teamsStore);
    const projectsArr = projectsData?.projects ?? [];
    const projectIds = projectsArr.map(({ projectId }) => ProjectId(projectId));
    const projectPlaceholders: { [id: number]: Team[] } = {};

    projectIds.forEach((projectId) => {
      const memberships =
        MembershipsStore.ofProject(projectId)(membershipsStore);
      const project_placeholders = memberships[1]?.placeholders ?? [];

      projectPlaceholders[projectId[1]] = project_placeholders;
    });
    const users = constructUserMeta({
      membershipsStore,
      usersStore,
      current_company_user_ids: private_user_ids,
      native_user_ids,
      active_user_ids,
      non_external_user_ids,
      current_company_active_user_ids: private_active_user_ids,
      projectIds,
      users_list,
      currentUser,
    });
    const companies = union(
      uniq(projectsArr.map((x) => x.customer.companyId).filter((x) => x >= 0)),
      [currentCompanyId],
    )
      .map(CompanyId)
      .map((x) => CompaniesStore.ofId(x)(companiesStore))
      .concat(
        CompaniesStore.toList(companiesStore).filter(
          (x) => x.companyType === CompanyType.Partner,
        ),
      )
      .filter(Boolean);
    const [task_fields, project_fields, project_phase_fields] = [
      FieldSectionType.Task,
      FieldSectionType.Project,
      FieldSectionType.ProjectPhase,
    ].map((type) => {
      return store.originals.ids
        .map((x) => store.originals.byId[x])
        .filter(({ objectType }) => objectType === type)
        .map((x) => ({
          ...x,
          type: filterableFieldType(x),
          sourceType:
            type === FieldSectionType.Task
              ? SourceType.Task
              : type === FieldSectionType.Project
              ? SourceType.Project
              : SourceType.ProjectPhase,
        }));
    }) as unknown as [TaskField[], ProjectField[], ProjectPhaseField[]];

    // @TODO remove this after testing indexed db implementation
    // commented bacauset the loader is not hiding since members store is empty
    const createdByUsers = {
      active_user_ids: [],
      by_project_id: {},
      by_user_id: {},
      non_external_user_ids: [],
    };

    const statusFieldOptions = (
      task_fields.find(
        (field) => (field as any).fieldName === 'Status',
      ) as unknown as SingleSelectField
    )?.metaData?.options;

    const filteredTaskFields = filterPointsFieldsFromTaskFields
      ? filterPointsFieldsFromTaskFields(task_fields)
      : task_fields;

    const baseFields = getBaseFields({
      users,
      teams,
      projectPlaceholders,
      translate,
      createdByUsers,
      projectsArr,
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      companies,
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      statusFieldOptions,
    });

    if (isSprintPlaningFeatureEnabled) {
      baseFields.push(
        AssociatedSprintsField(translate),
        CurrentSprintField(translate),
      );
    }

    if (isMultilineBudgetFeatureEnabled) {
      baseFields.push(BudgetsField(translate), BudgetNamesField(translate));
    }
    baseFields.push(EpicField(epicsArr ?? [], isEpicFeatureEnabled ?? false));

    const fields_with_metadata = filteredTaskFields
      .map((f) => ({ ...f, fieldName: camelCase(f.fieldName) }))
      .concat(baseFields)
      .map(
        (f) =>
          ({
            ...f,
            fieldLabel:
              (f as any).fieldLabel ??
              (translate
                ? translate(startCase(f.fieldName) as TranslationKey)
                : startCase(f.fieldName)),
            metaData: {
              ...f.metaData,
              users:
                f.fieldDataType === 'USER' && f.fieldName !== 'createdBy'
                  ? users
                  : (f.metaData as any)?.users,
            },
          } as TaskField),
      );

    const transformed_fields = [
      ...fields_with_metadata,
      ...project_fields,
      ...project_phase_fields,
    ].map((field): Field => {
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      switch (field.type) {
        case FieldType.SingleSelect:
        case FieldType.MultiSelect:
          return {
            // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
            ...field,
            metaData: {
              // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
              ...field.metaData,
              options: FieldHelper(field).options.allOptions,
            },
          };
        case FieldType.Assignee:
        case FieldType.Boolean:
        case FieldType.Date:
        case FieldType.Estimate:
        case FieldType.MultiUser:
        case FieldType.SingleUser:
        case FieldType.Number:
        case FieldType.Text:
        default:
          return field;
      }
    });

    const fields_lookup = keyBy(transformed_fields, 'fieldId');
    const ids = keys(fields_lookup).map(Number);

    return {
      byId: fromPairs(ids.map((x) => [x, Loaded(fields_lookup[x])])),
      originals: store.originals,
      ids: Loaded(ids),
      byObjectType: {
        [ObjectType.Task]: constructByObjectType(fields_with_metadata),
        [ObjectType.Project]: constructByObjectType(project_fields),
        [ObjectType.ProjectPhase]: constructByObjectType(project_phase_fields),
      },
    };
  };

const loaded = ({ ids: [status] }: FieldsStore) =>
  status === 'loaded' || status === 'refreshing';
const refreshing = ({ ids: [status] }: FieldsStore) => status === 'refreshing';

const toList = ({ ids: [, fields = []], byId }: FieldsStore) =>
  fields.map((x) => byId[x]).filter(Boolean);

const enabledTaskFields = ({
  byId,
  byObjectType: {
    [ObjectType.Task]: { enabled },
  },
}: FieldsStore) =>
  enabled
    .map((x) => byId[x])
    .filter(Boolean)
    .map(([, x]) => x)
    .filter(Boolean)
    .map((x) => {
      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      if (x?.fieldName === 'projectPhaseId') {
        return cloneDeep(x) as TaskField;
      }

      // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
      return { ...x } as TaskField;
    });

const allTaskFields = ({
  byId,
  byObjectType: {
    [ObjectType.Task]: { enabled },
  },
}: FieldsStore) =>
  enabled
    .map((x) => byId[x])
    .filter(Boolean)
    .map(([, x]) => x)
    .filter(Boolean)
    .map((x) => cloneDeep(x) as TaskField);

const getTaskFieldByName =
  (fieldName: string) =>
  ({
    byId,
    byObjectType: {
      [ObjectType.Task]: { byFieldName },
    },
  }: FieldsStore): EditableContent<TaskField> =>
    (byId[byFieldName[fieldName]] as any) ?? Empty();

const getProjectPhaseFieldByName =
  (fieldName: string) =>
  ({
    byId,
    byObjectType: {
      [ObjectType.ProjectPhase]: { byFieldName },
    },
  }: FieldsStore): EditableContent<TaskField> =>
    (byId[byFieldName[fieldName]] as any) ?? Empty();

export type StatusField = TaskField &
  SingleSelectField & {
    metaData: SingleSelectField['metaData'] & {
      options: StatusFieldOption[];
      default: number;
    };
  };

const getTaskStatusField = (store: FieldsStore): EditableContent<StatusField> =>
  getTaskFieldByName('status')(store) as EditableContent<StatusField>;

const getProjectPhaseStatusField = (
  store: FieldsStore,
): EditableContent<StatusField> =>
  getProjectPhaseFieldByName('Status')(store) as EditableContent<StatusField>;

const getProjectStatusFieldId = (store: FieldsStore): number =>
  store.byObjectType.PROJECT.byFieldName.Status;

export const FieldsStore = {
  empty: () => EMPTY,
  load,
  putAll,
  loaded,
  toList,
  enabledTaskFields,
  allTaskFields,
  add,
  remove,
  refresh,
  refreshing,
  getTaskFieldByName,
  getTaskStatusField,
  getProjectPhaseFieldByName,
  getProjectPhaseStatusField,
  getProjectStatusFieldId,
};
